import { AirlineLogo } from '@/src/utils/confirmation/airlineUtils'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'

interface PolicyAirline {
    airlineCode: string
    airlineName: string
    logoUrl: string
    values: Record<string, string>
}

interface PolicyComparisonProps {
    title: string
    airlines: PolicyAirline[]
    policyRows: { key: string; label: string }[]
}

const PolicyComparison: React.FC<PolicyComparisonProps> = ({ title, airlines, policyRows }) => {
    return (
        <Box sx={{ mb: 3 }}>
            {title && (
                <Typography
                    component="h2"
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' }, mb: 2 }}
                >
                    {title}
                </Typography>
            )}
            <TableContainer
                sx={{
                    border: 1,
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    maxWidth: '100%',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 }
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    position: 'sticky',
                                    left: 0,
                                    bgcolor: 'grey.100',
                                    zIndex: 1,
                                    fontWeight: 600,
                                    borderRight: 1,
                                    borderColor: 'grey.200',
                                    minWidth: 160,
                                    py: 1.5
                                }}
                            />
                            {airlines.map(airline => (
                                <TableCell
                                    key={airline.airlineCode}
                                    align="center"
                                    sx={{ bgcolor: 'grey.100', fontWeight: 600, minWidth: 170, py: 1.5 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                        <AirlineLogo airlineCode={airline.airlineCode} width={32} height={24} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {airline.airlineName}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {policyRows.map(row => (
                            <TableRow key={row.key}>
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        bgcolor: 'background.paper',
                                        zIndex: 1,
                                        fontWeight: 600,
                                        borderRight: 1,
                                        borderColor: 'grey.200',
                                        fontSize: '0.8125rem',
                                        py: 1.5
                                    }}
                                >
                                    {row.label}
                                </TableCell>
                                {airlines.map(airline => (
                                    <TableCell
                                        key={airline.airlineCode}
                                        align="left"
                                        sx={{ fontSize: '0.8125rem', color: 'text.secondary', py: 1.5, verticalAlign: 'top' }}
                                    >
                                        {airline.values[row.key] || '—'}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default PolicyComparison
export type { PolicyComparisonProps, PolicyAirline }
